import { createElement } from "react";
import { toast } from "sonner";
import { ImpersonationLockBadge } from "@src/features/app/impersonation/ImpersonationLockBadge";
import { ToastErrorInfo, AddToast } from "@src/lib/contexts/ToastContext";
import { track } from "./track";

export const addToast = (toastConfig: AddToast): string | number => {
  const { message, success = true, lifetimeMS } = toastConfig;
  if (success) {
    return toast.success(message, {
      duration: lifetimeMS,
    });
  } else {
    return toast.info(message, {
      duration: lifetimeMS,
    });
  }
};

const IMPERSONATION_TOAST_ID = "impersonation-read-only";
const IMPERSONATION_TOAST_MS = 4000;
// A screen's own error toast for the same blocked write lands right after
// this one; it is dropped inside this window so only the clear one shows.
const IMPERSONATION_REFUSAL_WINDOW_MS = 5000;
let lastImpersonationRefusalAt = 0;
let impersonationToastTimer: ReturnType<typeof setTimeout> | undefined;

const isWithinImpersonationRefusal = () =>
  Date.now() - lastImpersonationRefusalAt < IMPERSONATION_REFUSAL_WINDOW_MS;

/** Drops a screen's error or "saved" toast for a write blocked just now. */
export const markImpersonationRefusal = (): void => {
  lastImpersonationRefusalAt = Date.now();
};

let toastsHeldBack = false;

/**
 * Screens raise their error and "saved" toasts through sonner directly as
 * well as through addToast/addErrorToast. Inside the window after a blocked
 * write, either would contradict the read-only message, so both are held
 * back here, once for every caller, not at each call. Installed by the
 * write guard when the app's client is built, not on import, so a test that
 * mocks sonner keeps its mocks.
 */
export const holdBackToastsAfterRefusal = (): void => {
  if (toastsHeldBack) return;
  toastsHeldBack = true;
  const sonnerError = toast.error;
  const sonnerSuccess = toast.success;
  toast.error = ((...args: Parameters<typeof sonnerError>) =>
    isWithinImpersonationRefusal()
      ? IMPERSONATION_TOAST_ID
      : sonnerError(...args)) as typeof toast.error;
  toast.success = ((...args: Parameters<typeof sonnerSuccess>) =>
    isWithinImpersonationRefusal()
      ? IMPERSONATION_TOAST_ID
      : sonnerSuccess(...args)) as typeof toast.success;
};

/**
 * The toast for a write blocked while viewing as someone. One at a time: a
 * repeat replaces it and restarts its 4 s (sonner keeps the first timer on an
 * update, so the dismissal is ours).
 */
export const addImpersonationRefusalToast = (
  message: string,
): string | number => {
  markImpersonationRefusal();
  clearTimeout(impersonationToastTimer);
  impersonationToastTimer = setTimeout(
    () => toast.dismiss(IMPERSONATION_TOAST_ID),
    IMPERSONATION_TOAST_MS,
  );
  return toast(message, {
    id: IMPERSONATION_TOAST_ID,
    duration: Infinity,
    icon: createElement(ImpersonationLockBadge),
  });
};

export const addErrorToast = (errorInfo: ToastErrorInfo): string | number => {
  if (isWithinImpersonationRefusal()) {
    return IMPERSONATION_TOAST_ID;
  }
  const message =
    typeof errorInfo.message === "string"
      ? errorInfo.message
      : "An error occurred";
  track("error_toast_visible", {
    message,
    ...errorInfo,
    error: errorInfo,
    pathname: window.location.pathname,
  });

  return toast.error(
    errorInfo.message || "Something went wrong! Please try again.",
  );
};
